import { mdiAccountArrowRight, mdiPencil } from '@mdi/js';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionSummary,
  Drawer,
  IconButton,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import EmailFormContainer from 'Components/EmailForm/EmailForm.container';
import { popperStyles } from 'Components/Shared/ActionOnSelectedItems/popperStyles';
import { PersonContext } from 'Context/CurrentPerson';
import useTranslate from 'Hooks/useTranslate';
import TestIds from 'Tests/TestIds';
import Container from 'Theme/components/Container';
import { SvgIcon } from 'Theme/components/SvgIcon/SvgIcon';
import { createRef, useContext, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import UpdatePropertiesContainer from './UpdateProperties/UpdateProperties.container';

const useStyles = makeStyles(theme => ({
  drawer: {
    borderRadius: '20px 20px 0 0',
    maxHeight: '90%'
  },
  expansionPanel: {
    backgroundColor: 'transparent'
  },
  expansionPanelSummary: {
    cursor: 'auto',
    // Required for the text ellipsis
    overflow: 'hidden'
  },
  header: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    // Required for the text ellipsis
    overflow: 'hidden'
  },
  headerLeft: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  headerLeftIcon: {
    marginRight: theme.spacing(1),
    verticalAlign: 'middle'
  },
  headerRight: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    padding: `0 ${theme.spacing(3)}`
  },
  ...popperStyles(theme),
  paper: {
    padding: theme.spacing(3),
    width: '500px'
  },
  subscribeTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500
  }
}));

interface Props {
  selectedIds: string[];
  rowIndex: Record<string, IServiceSubscription>;
  selectedCount: number;
  disabled: boolean;
}
type DisplayAction = 'addSubscriptions' | 'updateProperties';

const ActionOnSelectedSubscriptions = (props: Props) => {
  const { selectedIds, rowIndex, selectedCount: count = 0, disabled } = props;
  const classes = useStyles();
  const ts = useTranslate();
  const [displayAction, setDisplayAction] = useState<DisplayAction | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorEl2, setAnchorEl2] = useState<null | HTMLElement>(null);
  const [popperOpen, setPopperOpen] = useState<boolean>(false);
  const [popperOpen2, setPopperOpen2] = useState<boolean>(false);
  const [arrowRef, setArrowRef] = useState<null | HTMLElement>(null);
  const [arrowRef2, setArrowRef2] = useState<null | HTMLElement>(null);
  const buttonRef = createRef<HTMLButtonElement>();
  const buttonRef2 = createRef<HTMLButtonElement>();
  const {
    currentPersonRoles: { isAccountAdmin }
  } = useContext(PersonContext);

  const isExpanded = false;
  const onCollapse = () => {
    setDisplayAction(null);
    setPopperOpen(false);
    setPopperOpen2(false);
    setAnchorEl2(null);
    setAnchorEl(null);
  };

  return <>
    {count > 0 && <div style={{ height: '72px' }} />}
    <Drawer
      anchor="bottom"
      variant={isExpanded ? 'temporary' : 'persistent'}
      open={count > 0}
      classes={{ paper: classes.drawer }}
    >
      <Accordion
        expanded={isExpanded}
        classes={{ root: classes.expansionPanel }}
      >
        <AccordionSummary
          classes={{ content: classes.expansionPanelSummary }}
          expandIcon={
            !isExpanded ? null : (
              <ExpandMoreIcon
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (displayAction) {
                    setDisplayAction(null);
                  }
                }}
              />
            )
          }
          component="div"
        >
          <div className={classes.header}>
            <Typography
              variant="body2"
              color="textSecondary"
              classes={{ root: classes.headerLeft }}
            >
              <BubbleChartIcon
                color="primary"
                classes={{ root: classes.headerLeftIcon }}
              />
              <FormattedMessage
                id="page.services.index.selectedServiceCount"
                values={{ count }}
              />
            </Typography>
            <div className={classes.headerRight}>
              {isAccountAdmin && (
                <>
                  <Tooltip
                    title={
                      <FormattedMessage id="page.services.action.subscribeExternalUser" />
                    }
                  >
                    <IconButton
                      ref={buttonRef}
                      color={
                        displayAction === 'addSubscriptions'
                          ? 'primary'
                          : 'default'
                      }
                      onClick={() => {
                        setDisplayAction('addSubscriptions');
                        setPopperOpen(true);
                        setPopperOpen2(false);
                        setAnchorEl(buttonRef.current);
                      }}
                      disabled={disabled}
                      data-testid={
                        TestIds.pages.services.addExternalUserButton
                      }
                      size="large">
                      <SvgIcon
                        path={mdiAccountArrowRight}
                        fill="#666666"
                        width="24"
                      />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      <FormattedMessage id="page.services.action.updateServicesProperties" />
                    }
                  >
                    <IconButton
                      ref={buttonRef2}
                      color={
                        displayAction === 'updateProperties'
                          ? 'primary'
                          : 'default'
                      }
                      onClick={() => {
                        setDisplayAction('updateProperties');
                        setPopperOpen(false);
                        setPopperOpen2(!popperOpen2);
                        setAnchorEl2(buttonRef2.current);
                      }}
                      disabled={disabled}
                      data-testid={
                        TestIds.pages.services.updateProperties.featureButton
                      }
                      size="large">
                      <SvgIcon path={mdiPencil} fill="#666666" width="24" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </div>
          </div>
        </AccordionSummary>

        {anchorEl && displayAction === 'addSubscriptions' && (
          <Popper
            open={popperOpen}
            placement="top"
            anchorEl={anchorEl}
            modifiers={[
              {
                name: 'arrow',
                enabled: true,
                options: {
                  element: arrowRef
                }
              }
            ]}
            className={classes.popper}
          >
            <span
              className={classes.arrow}
              ref={element => {
                setArrowRef(element);
              }}
            />
            <Helmet title={ts('page.services.createSubscription.title')} />
            <Container>
              <Paper classes={{ root: classes.paper }}>
                <Typography variant="h5" className={classes.subscribeTitle}>
                  {ts('page.services.action.subscribeExternalUser')}
                </Typography>
                <EmailFormContainer
                  serviceSubscriptions={selectedIds.map(s => JSON.parse(s))}
                  onCancel={onCollapse}
                  onSubmit={onCollapse}
                />
              </Paper>
            </Container>
          </Popper>
        )}

        {anchorEl2 && displayAction === 'updateProperties' && (
          <Popper
            open={popperOpen2}
            placement="top"
            anchorEl={anchorEl2}
            modifiers={[
              {
                name: 'arrow',
                enabled: true,
                options: {
                  element: arrowRef2
                }
              }
            ]}
            className={classes.popper}
          >
            <span
              className={classes.arrow}
              ref={element => {
                setArrowRef2(element);
              }}
            />
            <UpdatePropertiesContainer
              selectedServiceIds={selectedIds}
              onCollapse={onCollapse}
              rowIndex={rowIndex}
            />
          </Popper>
        )}
      </Accordion>
    </Drawer>
  </>;
};

export default ActionOnSelectedSubscriptions;
